import { doNothing } from "@edgetier/utilities";
import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mergeRefs } from "react-laag";
import { transformValues } from "./select.utilities";
import { useSelectSetup } from "./utilities/use-select-setup";
import SelectContext from "./select-context/select-context";
import SelectMenu from "./select-menu";
import {
    APPLY_BUTTON_TEXT,
    CANCEL_BUTTON_TEXT,
    ENTER_SELECTED_LIST_KEY,
    INLINE_DROPDOWN_STYLE,
    INLINE_MENU_PROPS,
    MOVE_TO_ACTIONS_KEY,
    OPEN_KEY,
    PLACEMENT_MAP,
    REMOVE_LAST_SELECTED_KEY,
    SELECT_ACTION_SELECTOR,
    SELECTED_ITEM_SELECTOR,
} from "./select.constants";
import { useIsInputOverflowed } from "./utilities/use-is-input-overflowed";
import { useScrollMenuIntoView } from "./utilities/use-scroll-menu-into-view";
import { useSelectPreview } from "./utilities/use-select-preview";
import { DropdownIcon, Menu, Icon, Input, Tooltip } from "@edgetier/global-components";
import { IProps } from "./select.types";
import classNames from "classnames";
import styles from "./select.module.scss";
import { kebabCase, omit } from "lodash";
import { SelectPreviewTooltip } from "./select-preview-tooltip";
import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";

/**
 * Custom searchable select component.
 * @param props.addItemMenu                       A react component that will contain the menu to add an item.
 * @param props.addItemButtonProps                Props to pass to the add item button.
 * @param props.children                          Optional function of how to render items.
 * @param props.closeOnDisappear                  Whether the select should close when the trigger is no longer visisble.
 * @param props.description                       Description of the items being displayed.
 * @param props.disableMenuItems                  Whether the select menu items should be disabled or not.
 * @param props.className                         Additional classes to add to the select.
 * @param props.getItemOrder                      Getter function to the item order from any item.
 * @param props.getGroup                          Getter function to the group name from any item.
 * @param props.getGroupOrder                     Getter function to the group order from any item.
 * @param props.getGroupItemOrder                 Getter function to the group item order from any item.
 * @param props.getLabel                          Getter function to the label from any item.
 * @param props.getPreviewLabel                   Getter function to the preview label from any item.
 * @param props.getValue                          Getter function to the value from any item.
 * @param props.getIcon                           Getter function to the icon from any item.
 * @param props.getIconProps                      Getter function to extra icon props, e.g. colour, from any item.
 * @param props.hasBorder                         Whether the select should have a border or not.
 * @param props.isAllFilter                       When true, display "all" label for an empty filter.
 * @param props.isClearable                       If the user is allowed remove all values.
 * @param props.isCompact                         Whether the select should be compact or not.
 * @param props.isMedium                          Whether the select should be medium or not.
 * @param props.isDisabled                        Whether the component can be interacted with or not.
 * @param props.isEmptyLabel                      Label to show when no items are selected.
 * @param props.isLoading                         Items loading state.
 * @param props.initialIsOpen                     Open the menu on the first render and focus its search input, e.g. when the select is shown inside a popup. `onOpen` is not called for this initial open, and the initial selection is read once at mount, so options that load asynchronously need `selectedItems` passed alongside it.
 * @param props.inputId                           The ID of the input element.
 * @param props.isSearchable                      Whether to show a search box or not.
 * @param props.labelledBy                        The id of the element that labels the select.
 * @param props.isSingleSelect                    When true, only one item can be selected.
 * @param props.isSortedAlready                   Only when false should the items be sorted by label.
 * @param props.isSourcedSelect                   Whether the select requests it's options or gets them passed to it.
 * @param props.items                             Options the user may choose from.
 * @param props.menuShouldScrollIntoView          Whether the menu should scroll into view when it's opened.
 * @param props.matchWidthToField                 Whether the menu should match the width of the field.
 * @param props.minimumWidth                      The minimum width of the menu.
 * @param props.noItemsFoundLabel                 Label to show when no items are found.
 * @param props.onClear                           A function that gets called when the select is cleared.
 * @param props.onClose                           A function that gets called when the select is closed.
 * @param props.onOpen                            Optional callback when the menu is opened.
 * @param props.onInputChange                     A function that gets called when the select's input value changes.
 * @param props.onSelect                          Handle the user selecting item(s).
 * @param props.message                           A message to show when the select is loading.
 * @param props.placeholder                       Search box placeholder text.
 * @param props.previewPlaceholder                Custom content passed to select as placeholder.
 * @param props.selectedValues                    Currently selected values.
 * @param props.useAllItemsSelectedPreviewMessage Where the select preview should say "All items selected" in preview.
 * @param props.placement                         Where the select menu should placed when it's open.
 * @param props.possiblePlacements                The options for where the menu can be placed.
 * @param props.overflowTooltipPlacement          Optional placement of the tooltip being shown when the selected value is overflowing.
 * @param props.menuDropdownProps                 Optional props forwarded to Menu.Dropdown (e.g. w, miw, style). Prefer one clear source of width versus width/matchWidthToField on Select, which set the parent Menu width.
 * @param props.inline                            Render the menu in flow under the input, as part of a surrounding popover, instead of floating. The surrounding surface owns closing on outside clicks and keys, so the Select's own outside-close and Tab jump to its buttons are skipped. Default false.
 */
const Select = <IItem extends {}, IValue extends {} = string>({
    addItemMenu,
    addItemButtonProps,
    children,
    closeOnDisappear = false,
    description,
    disableMenuItems = false,
    className,
    getItemOrder,
    getGroup,
    getGroupOrder,
    getGroupItemOrder,
    getLabel,
    getPreviewLabel = getLabel,
    getValue,
    getIcon,
    getIconProps,
    isItemDisabled: propIsItemDisabled,
    hasBorder = false,
    isAllFilter = false,
    isClearable = false,
    isCompact = false,
    isMedium: initialIsMedium = false,
    isDisabled = false,
    isEmptyLabel,
    isLoading = false,
    initialIsOpen = false,
    inputId,
    isSearchable = true,
    labelledBy,
    isSingleSelect,
    isSortedAlready = false,
    isSourcedSelect = false,
    items: initialItems,
    menuShouldScrollIntoView = false,
    matchWidthToField,
    minimumWidth,
    noItemsFoundLabel,
    onClear: propOnClear = doNothing,
    onClose = doNothing,
    onOpen = doNothing,
    onInputChange,
    onSelect,
    message,
    placeholder = `Search ${description.toLowerCase()}s...`,
    previewPlaceholder,
    selectedValues: initialSelectedValues,
    selectedItems: initialSelectedItems,
    useAllItemsSelectedPreviewMessage = true,
    placement = "bottom-start",
    possiblePlacements,
    isMultipleSelectCheckbox,
    enableAutoFocus = true,
    overflowTooltipPlacement,
    disableInternalFilter,
    isPaginated = false,
    required = false,
    icon,
    iconProps,
    variant = "default",
    withinPortal,
    target,
    activable,
    disabledItems: disabledItemsProp,
    width,
    readOnly,
    menuDropdownProps,
    inline = false,
    ...other
}: IProps<IItem, IValue>) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // A select shown inside a popup opens straight away, so the user can type to search without clicking first.
    useEffect(() => {
        if (initialIsOpen) {
            inputRef.current?.focus();
        }
        // Only on mount: later changes to the prop must not move focus.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The same condition that draws the selected list, because there is nothing to move into without it.
    const hasSelectedList = !isSingleSelect && !isMultipleSelectCheckbox;

    // Locked open means that the menu is open and events that usually close it do not. An example use case: when there
    // is a button to add a new select option, it opens a modal and the menu is locked open while the modal is open.
    const [isLockedOpen, setIsLockedOpen] = useState(false);

    // We use Downshift's internal isOpen as a signal of when to open the menu but masterIsOpen has the final say and
    // will be used to close the menu.
    const [masterIsOpen, setMasterIsOpen] = useState(false);

    // Create isItemDisabled function that disables items in the unselected list for multi-select
    const isItemDisabled = useMemo(
        () => (item: IItem) => {
            // First check if there's a custom isItemDisabled function
            if (propIsItemDisabled?.(item)) {
                return true;
            }

            // For multi-select (not isSingleSelect), disable items that are in the disabledItems list
            if (!isSingleSelect && disabledItemsProp && getValue) {
                const itemValue = getValue(item);
                return disabledItemsProp.includes(itemValue);
            }

            return false;
        },
        [propIsItemDisabled, isSingleSelect, disabledItemsProp, getValue]
    );

    // Use only items that have a label and maybe them alphabetically.
    const items = useMemo(() => {
        const labelledItems = (Array.isArray(initialItems) ? initialItems : []).filter(
            (item) => typeof getLabel(item) === "string"
        );

        if (isSortedAlready) {
            if (typeof getGroup === "function") {
                return labelledItems.sort((itemOne, itemTwo) => getGroup(itemOne).localeCompare(getGroup(itemTwo)));
            }
            return labelledItems;
        }

        return labelledItems
            .slice()
            .sort((itemOne, itemTwo) => getLabel(itemOne).localeCompare(getLabel(itemTwo)))
            .sort((itemOne, itemTwo) =>
                typeof getItemOrder === "function" ? getItemOrder(itemOne) - getItemOrder(itemTwo) : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group if any
                typeof getGroup === "function" ? getGroup(itemOne).localeCompare(getGroup(itemTwo)) : 0
            )
            .sort((itemOne, itemTwo) =>
                // Sort by group order if exists
                typeof getGroupOrder === "function" ? getGroupOrder(itemOne) - getGroupOrder(itemTwo) : 0
            )
            .sort((itemOne, itemTwo) => {
                if (typeof getGroup === "function" && getGroup(itemOne) === getGroup(itemTwo)) {
                    // Sort by group item order if exists
                    return typeof getGroupItemOrder === "function"
                        ? getGroupItemOrder(itemOne) - getGroupItemOrder(itemTwo)
                        : 0;
                }
                return 0;
            });
    }, [getLabel, getGroup, initialItems, isSortedAlready, getItemOrder, getGroupOrder, getGroupItemOrder]);

    // Make sure the selected values are always an array.
    const selectedValues = useMemo(() => transformValues(initialSelectedValues), [initialSelectedValues]);

    // Make sure the selected items are always an array.
    const selectedItems = useMemo(() => {
        const transformed = transformValues(initialSelectedItems ?? null);
        return transformed.length ? transformed : undefined;
    }, [initialSelectedItems]);

    // Get the currently selected items. These are the items that the user has clicked "Apply" for. This variable only
    // changes when the user clicks "Apply". It does not change when an option is selected from the dropdown before
    // clicking "Apply".
    const itemsSelectedBeforeOpen = useMemo(
        () => (selectedItems ?? items).filter((item) => selectedValues.includes(getValue(item))),
        [getValue, items, selectedValues, selectedItems]
    );

    const clear = () => {
        setInputValue("");
        onSelectItems([], []);
        propOnClear();
    };

    /**
     * Close the menu.
     */
    const close = (options: { blur?: boolean } = { blur: true }) => {
        setInputValue("");
        setMasterIsOpen(false);

        // Sometimes, we end up in weird states where downshiftIsOpen is true while our isOpen is false. Calling this
        // when we close the menu will update Downshift's internal isOpen state to be false.
        downShiftCloseMenu();

        // Someone who was still inside the menu when it closed finished on purpose, with Done or Cancel, so the
        // keyboard goes back to the field they came from. Letting it fall to the top of the page instead would mean
        // tabbing through everything above to reach whatever comes next.
        if (menuRef.current?.contains(document.activeElement)) {
            inputRef.current?.focus();
        } else if (typeof options.blur === "undefined" || options.blur) {
            inputRef.current?.blur();
        }

        onClose();
    };

    /**
     * Handle a user selected one or more values. If this is a single selection a single item is removed from the array
     * of selected values.
     * @param newSelectedValues Values the user has selected.
     */
    const onSelectItems = (newSelectedValues: IValue[], newSelectedItems: IItem[]) => {
        if (isSingleSelect) {
            onSelect(newSelectedValues[0] ?? null, newSelectedItems[0] ?? null);
        } else {
            onSelect(newSelectedValues, newSelectedItems);
        }
    };

    /**
     * Open or close the menu.
     */
    const onIsOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isDisabled) {
                return;
            }
            if (isOpen) {
                onOpen();
            } else {
                onClose();
            }
        },
        [isDisabled, onClose, onOpen]
    );

    const itemLimit = useMemo(
        () =>
            typeof other.selectedItemsLimit === "number" ? other.selectedItemsLimit : other.selectedItemsLimit?.limit,
        [other.selectedItemsLimit]
    );

    const { comboboxProps, multiplSelectionProps, setInputValue, selectMenuProps, menuContainerRef } = useSelectSetup({
        close,
        disableMenuItems,
        getLabel,
        getValue,
        initialIsOpen,
        items,
        inputId,
        selectedValues,
        selectedItems,
        isMultipleSelectCheckbox,
        isSingleSelect,
        isSourcedSelect,
        isItemDisabled,
        onInputChange,
        clear,
        onSelectItems,
        onIsOpenChange,
        itemLimit,
        virtualized: other.virtualized,
        disableInternalFilter,
        inline,
    });
    const { isOpen: downshiftIsOpen, closeMenu: downShiftCloseMenu } = comboboxProps;

    const isOpen = useMemo(
        () => isLockedOpen || downshiftIsOpen || masterIsOpen,
        [downshiftIsOpen, isLockedOpen, masterIsOpen]
    );

    const selectPreview = useSelectPreview({
        description,
        getLabel: getPreviewLabel,
        isAllFilter,
        isLoading,
        isPaginated,
        isEmptyLabel,
        searchPlaceholder: placeholder,
        isSingleSelect: isSingleSelect ?? false,
        items,
        isOpen,
        previewPlaceholder,
        itemsSelectedBeforeOpen,
        useAllItemsSelectedPreviewMessage,
        hasNextPage: other.hasNextPage,
    });

    /**
     * Handle the blur event. If the user clicks outside the select, close it. If the user clicks on an item inside the
     * menu, don't close and focus the input field.
     * @param event The blur event.
     */
    const onBlur = (event: React.FocusEvent) => {
        if (isLockedOpen) {
            return;
        }

        const relatedTarget = event.relatedTarget as HTMLElement | null;
        const innerText = relatedTarget?.innerText;

        const focusMovedIntoMenu = Boolean(relatedTarget && menuRef.current?.contains(relatedTarget));

        const isApplyOrCancel = innerText === APPLY_BUTTON_TEXT || innerText === CANCEL_BUTTON_TEXT;

        // Returning focus to the search box exists for the mouse: clicking inside the menu would otherwise leave the
        // user unable to keep typing. The selected list is the one place focus is meant to settle, so it is asked of
        // the element taking focus rather than of any state, which cannot then disagree with where the focus is.
        const focusMovedToSelectedList = Boolean(relatedTarget?.closest(SELECTED_ITEM_SELECTOR));

        if (focusMovedIntoMenu && !isApplyOrCancel) {
            if (!isSingleSelect && !focusMovedToSelectedList) {
                inputRef.current?.focus();
            }
            return;
        }
        if (!isApplyOrCancel) {
            close();
        }
    };

    /**
     * Answer the two search box keys that belong to the selected list, which sits to the right of the options.
     *
     * Only the multiple select draws that list, so only it answers these. Both keys wait until the caret has nowhere
     * further to go, so that they still edit the search text while there is any left to edit.
     * @param event The keyboard event from the search box.
     */
    const onDropdownKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { selectionEnd, selectionStart, value } = event.currentTarget;
        const { selectedItems, setActiveIndex, removeSelectedItem } = multiplSelectionProps;

        // Enter on a closed select opens it. Without this the key reaches the surrounding form and submits it, which
        // is a surprising thing for a closed dropdown to do to someone who arrived on it with the tab key.
        if (event.key === OPEN_KEY && !isOpen) {
            event.preventDefault();

            return comboboxProps.openMenu();
        }

        // Tab hands the keyboard to the menu's own buttons while it is open. They are drawn in a portal, so the tab
        // order steps straight past them to the next field on the page, which leaves no way to apply a selection
        // without the mouse. An inline menu is not in a portal: its buttons are already next in the tab order, and
        // jumping to them would skip the bulk buttons and the lists in between.
        if (event.key === MOVE_TO_ACTIONS_KEY && !event.shiftKey && isOpen && !inline) {
            const action = menuRef.current?.querySelector<HTMLElement>(SELECT_ACTION_SELECTOR);

            if (action !== null && typeof action !== "undefined") {
                event.preventDefault();

                return action.focus();
            }
        }

        // The menu is kept mounted when it is closed, so its rows are still there to be found. Nothing below should
        // answer for a select the user cannot see.
        if (!hasSelectedList || !isOpen || selectedItems.length === 0) {
            return;
        }

        // Move into the list, once the caret has nowhere further right to go. A search can leave the list showing none
        // of the selected items, and then there is nowhere to move to, so the key is left alone.
        if (event.key === ENTER_SELECTED_LIST_KEY && selectionStart === value.length && selectionEnd === value.length) {
            if (selectMenuProps.selectedSearchMatches?.length === 0) {
                return;
            }

            event.preventDefault();

            // The top of the list, because the user is arriving at a column they read downwards. Downshift moves the
            // focus itself, but only when its index changes, and it keeps that index across a close of the menu. So
            // the row is focused here instead, and Downshift is told where the keyboard went.
            setActiveIndex(0);

            return menuRef.current?.querySelector<HTMLElement>(SELECTED_ITEM_SELECTOR)?.focus();
        }

        // Remove the last chosen item from an empty search box, the usual multiple select gesture.
        if (event.key === REMOVE_LAST_SELECTED_KEY && value.length === 0) {
            event.preventDefault();

            return removeSelectedItem(selectedItems[selectedItems.length - 1]);
        }
    };

    /**
     * Hand the keyboard back to the search box from the first of the menu's buttons.
     *
     * Tab walks forward through the buttons on its own, because they sit together in the menu. Only the way back to
     * the search box needs saying, since the menu is drawn in a portal and the search box is not in front of it.
     * @param event The keyboard event from inside the menu.
     */
    /**
     * Send the up and down arrows back to the search box when they are pressed elsewhere in the menu.
     *
     * The menu answers them by putting the focus itself on an option. The options are not driven that way: they are
     * highlighted while the search box keeps the keyboard, so an option holding the focus is a state nothing else
     * understands, and it is lost altogether the moment that option is chosen and leaves the list. The selected list
     * is left alone, because it does own these keys and stops them before this runs.
     *
     * The keyboard is only handed back, and the highlight is left to the next press. Moving it from here means
     * telling Downshift where it should be, and a highlight put there rather than walked to looks the same but is
     * not one Enter will act on.
     * @param event The keyboard event from inside the menu.
     */
    const onMenuKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const isOnSelectedItem =
            event.target instanceof HTMLElement && event.target.closest(SELECTED_ITEM_SELECTOR) !== null;

        if ((event.key !== "ArrowDown" && event.key !== "ArrowUp") || isOnSelectedItem) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        inputRef.current?.focus();
    };

    const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // `menuDropdownProps` is passed through to callers, so anything they put on the dropdown runs first.
        menuDropdownRest.onKeyDown?.(event);

        const firstAction = menuRef.current?.querySelector<HTMLElement>(SELECT_ACTION_SELECTOR);

        // Shift+Tab from the first button undoes that jump, so an inline menu, which never made it, is left alone.
        if (inline || event.key !== MOVE_TO_ACTIONS_KEY || !event.shiftKey || document.activeElement !== firstAction) {
            return;
        }

        event.preventDefault();
        inputRef.current?.focus();
    };

    const inputProps = comboboxProps.getInputProps({
        // Downshift's own search box keys stay off. They are written for chosen items sitting to the left of the
        // input, so they answer this layout backwards, and they apply to every kind of select, including the ones
        // with no selected list to move around. `preventKeyAction` drops the handler passed here, so the search
        // box's own keys are attached after it rather than through it.
        ...multiplSelectionProps.getDropdownProps({
            preventKeyAction: true,
            autoComplete: "off",
            disabled: isDisabled,
            readOnly,
            onBlur,
            "aria-labelledby": labelledBy,
            "aria-required": required,
        }),
        onKeyDown: onDropdownKeyDown,
    });

    const isOverflowed = useIsInputOverflowed(inputRef.current);

    useScrollMenuIntoView(menuShouldScrollIntoView, isOpen, inputRef.current);

    const menuDropdownUserRef = menuDropdownProps?.ref;
    const menuDropdownUserClassName = menuDropdownProps?.className;
    const menuDropdownRest = omit(menuDropdownProps ?? {}, ["ref", "className"]);

    return (
        <SelectContext.Provider
            value={{
                close,
                lockOpen: () => setIsLockedOpen(true),
                unlock: () => setIsLockedOpen(false),
            }}
        >
            <Menu
                opened={isOpen}
                // The search box closes the menu when it loses the keyboard to somewhere outside, which was enough
                // while it always held the keyboard. Tab can now hand it to the menu's own buttons, and a click
                // outside then never reaches the search box at all. The menu sees those clicks wherever the keyboard
                // is, so it closes them, and the search box is left to the case it still handles itself.
                //
                // Only a multi-select needs this. Its own buttons are the reason the keyboard can sit somewhere the
                // search box never hears about, and only a multi-select renders them. A single-select instead closes
                // on the item click itself, which arrives here with the keyboard on the clicked item, and closing a
                // second time from under Downshift throws away the selection it is still in the middle of making.
                //
                // An inline menu is left to the surface it sits in. The menu counts a key pressed outside it as a click
                // outside, so Tab on one of that surface's own controls would close the list under it, and that
                // surface already owns the tab order and when everything closes.
                onClose={() => !inline && !isSingleSelect && document.activeElement !== inputRef.current && close()}
                closeOnItemClick={isSingleSelect ?? false}
                returnFocus={false}
                withinPortal={withinPortal}
                keepMounted
                trapFocus={false}
                // Inline, the dropdown takes its parent's width (`INLINE_DROPDOWN_STYLE`), so no menu width applies.
                width={inline ? undefined : matchWidthToField ? "target" : width}
                position={placement}
                {...(inline ? INLINE_MENU_PROPS : {})}
            >
                <Tooltip
                    label={
                        <SelectPreviewTooltip<IItem, IValue>
                            selectedItems={itemsSelectedBeforeOpen}
                            getLabel={getLabel}
                            getValue={getValue}
                            description={description}
                        />
                    }
                    openDelay={250}
                    disabled={!isOverflowed || itemsSelectedBeforeOpen.length === 0 || isOpen}
                    position={overflowTooltipPlacement ? PLACEMENT_MAP[overflowTooltipPlacement] : undefined}
                    withArrow
                >
                    {/* The surrounding tooltip clones this target with a key handler of its own, which then replaces
                    the input's handler rather than composing with it. Passing the input's handler through the target
                    makes the tooltip compose the two, so Downshift keeps its arrow key navigation. */}
                    <Menu.Target
                        data-testid={`select-menu-target-${kebabCase(inputId)}`}
                        onKeyDown={inputProps.onKeyDown}
                    >
                        {target && isValidElement(target) ? (
                            cloneElement(target, { ...inputProps, ref: mergeRefs(inputProps.ref, inputRef) })
                        ) : (
                            <Input
                                {...other.inputProps}
                                {...inputProps}
                                readOnly={readOnly}
                                variant={variant}
                                placeholder={selectPreview}
                                value={
                                    inputProps.value.length > 0
                                        ? inputProps.value
                                        : itemsSelectedBeforeOpen.length > 0 && !isOpen
                                          ? selectPreview
                                          : ""
                                }
                                ref={mergeRefs(inputProps.ref, inputRef)}
                                leftSection={icon && <Icon icon={icon} />}
                                rightSection={
                                    isLoading ? (
                                        <Icon icon={faSpinnerThird} size="xxs" spin={isLoading} {...iconProps} />
                                    ) : (
                                        // An inline list is always open under the input, so there is nothing to toggle.
                                        !inline && <DropdownIcon isOpen={isOpen} {...iconProps} />
                                    )
                                }
                                isClearable={isClearable && itemsSelectedBeforeOpen.length > 0}
                                onClear={clear}
                                pointer
                                activable={activable}
                            />
                        )}
                    </Menu.Target>
                </Tooltip>
                <Menu.Dropdown
                    {...menuDropdownRest}
                    onKeyDown={onMenuKeyDown}
                    ref={mergeRefs(menuRef, menuContainerRef, menuDropdownUserRef)}
                    data-inline={inline || undefined}
                    style={inline ? [menuDropdownRest.style, INLINE_DROPDOWN_STYLE] : menuDropdownRest.style}
                    className={classNames(
                        styles["select"],
                        {
                            [styles["select--is-single-select"]]: isSingleSelect,
                            [styles["select--is-multiple-select"]]: !isSingleSelect,
                            [styles["select--inline"]]: inline,
                        },
                        menuDropdownUserClassName
                    )}
                >
                    <SelectMenu<IItem, IValue>
                        onKeyDownCapture={onMenuKeyDownCapture}
                        addItemMenu={addItemMenu}
                        addItemButtonProps={addItemButtonProps}
                        description={description}
                        getGroup={getGroup}
                        isLoading={isLoading}
                        message={message}
                        noItemsFoundLabel={noItemsFoundLabel}
                        onMassSelect={() => setInputValue("")}
                        isMultipleSelectCheckbox={isMultipleSelectCheckbox}
                        enableAutoFocus={enableAutoFocus}
                        isCompact={isCompact}
                        itemsSelectedBeforeOpen={itemsSelectedBeforeOpen}
                        isPaginated={isPaginated}
                        getIcon={getIcon}
                        getIconProps={getIconProps}
                        {...selectMenuProps}
                        {...other}
                        close={close}
                    >
                        {children}
                    </SelectMenu>
                </Menu.Dropdown>
            </Menu>
        </SelectContext.Provider>
    );
};

export default Select;
